import { useAnalytics } from 'contexts/Analytics/Analytics';
import { useApi } from 'contexts/Api/Api';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import AuthModalButton from '../AuthModalButton/AuthModalButton';
import {
    AuthSwitchButton,
    Backdrop,
    CheckboxWrapper,
    CloseButton,
    Container,
    Content,
    Form,
    Loader,
    LoaderCircle,
    ModalDivider,
    Notification,
    OAuthButton,
    OAuthIconWrap,
    PrimaryButton,
    Row,
    Text,
    TextField,
    Title,
} from './AuthModal.styled';

function resolveOAuthApiBaseUrl(configuredValue: string | undefined): string | null {
    if (typeof window === 'undefined') {
        return null;
    }

    const host = window.location.hostname.toLowerCase();
    const configured = (configuredValue || '').trim();

    const knownHostApiBase = (() => {
        if (host === 'staging.kromatic.com') {
            return 'https://staging-api.kromatic.com';
        }
        if (host === 'kromatic.com' || host === 'www.kromatic.com') {
            return 'https://api.kromatic.com';
        }
        return null;
    })();

    if (!configured) {
        return knownHostApiBase;
    }

    if (/^https?:\/\//i.test(configured)) {
        try {
            const parsed = new URL(configured);
            if (
                knownHostApiBase &&
                (parsed.hostname === 'staging.kromatic.com' ||
                    parsed.hostname === 'kromatic.com' ||
                    parsed.hostname === 'www.kromatic.com') &&
                parsed.pathname.startsWith('/api')
            ) {
                return knownHostApiBase;
            }
            return configured.replace(/\/+$/, '');
        } catch {
            return knownHostApiBase;
        }
    }

    if (configured.startsWith('/')) {
        if (knownHostApiBase) {
            return knownHostApiBase;
        }
        return `${window.location.origin}${configured}`.replace(/\/+$/, '');
    }

    return knownHostApiBase;
}

function resolveForgotPasswordUrl(email: string): string {
    // Shared reset page lives on orca-front. Mirror the repo's staging-vs-prod
    // host convention (see Api.tsx resolveAuthApiBaseUrl): staging FE host →
    // staging.kromatic.com, everything else → kromatic.com.
    const base =
        typeof window !== 'undefined' && window.location.hostname === 'staging.kromatic.com'
            ? 'https://staging.kromatic.com'
            : 'https://kromatic.com';
    const url = `${base}/auth/forgot-password`;
    return email ? `${url}?email=${encodeURIComponent(email)}` : url;
}

function GoogleIcon() {
    return (
        <OAuthIconWrap aria-hidden>
            <svg height="20" viewBox="0 0 18 18" width="20">
                <path
                    d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.2 1.12-.84 2.06-1.8 2.7v2.24h2.92c1.72-1.58 2.68-3.92 2.68-6.58z"
                    fill="#4285F4"
                />
                <path
                    d="M9 18c2.44 0 4.5-.8 6-2.22l-2.92-2.24c-.8.54-1.82.86-3.08.86-2.36 0-4.36-1.58-5.08-3.72H.92v2.3A9 9 0 0 0 9 18z"
                    fill="#34A853"
                />
                <path
                    d="M3.92 10.68A5.4 5.4 0 0 1 3.64 9c0-.58.1-1.14.28-1.68V5.02H.92A9 9 0 0 0 0 9c0 1.44.34 2.8.92 3.98l3-2.3z"
                    fill="#FBBC05"
                />
                <path
                    d="M9 3.58c1.34 0 2.54.46 3.48 1.36l2.6-2.6C13.5.9 11.44 0 9 0A9 9 0 0 0 .92 5.02l3 2.3C4.64 5.18 6.64 3.58 9 3.58z"
                    fill="#EA4335"
                />
            </svg>
        </OAuthIconWrap>
    );
}

export default function AuthModal({ screen = 'login', onClose }: AuthModalProps) {
    const [errors, setErrors] = useState<Record<string, string> | null>(null);
    const [loading, setLoading] = useState(false);
    const [notification, setNotification] = useState(false);
    const [recoveryEmail, setRecoveryEmail] = useState<string | null>(null);
    const [loginEmail, setLoginEmail] = useState('');
    const [show, setShow] = useState<AuthModalScreen>(screen);
    const api = useApi();
    const { trackActionEvent } = useAnalytics();

    const screenMode = (currentScreen: AuthModalScreen): 'login' | 'join' =>
        currentScreen.startsWith('registration') ? 'join' : 'login';

    useEffect(() => {
        void trackActionEvent('signup_modal_opened', {
            auth_state: 'guest',
            resource_type: 'tool',
            source: 'auth_modal',
            screen: screenMode(screen),
        });
        // Fire once on the hidden→shown transition (modal mounts when shown).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClose = useCallback(() => {
        if (!loading) {
            onClose();
        }
    }, [loading, onClose]);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleClose();
            }
        },
        [handleClose]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    useEffect(() => {
        if (notification) {
            const timeout = setTimeout(() => setNotification(false), 5000);

            return () => {
                clearTimeout(timeout);
            };
        }
    }, [notification]);

    useEffect(() => {
        setErrors(null);
        setLoginEmail('');
        if (show !== 'recoveryVerify') {
            setRecoveryEmail(null);
        }
    }, [show]);

    async function handleRecoveryResend() {
        const response = await request('recoverySend', {
            email: recoveryEmail,
        });

        if (response) {
            setNotification(true);
        }
    }

    async function handleSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();

        if (show === 'login' || show.startsWith('registration')) {
            void trackActionEvent('signup_form_submitted', {
                auth_state: 'guest',
                resource_type: 'tool',
                source: 'auth_modal',
                screen: screenMode(show),
            });
        }

        const formData = new FormData(e.currentTarget);
        const values: any = Object.fromEntries(formData.entries());
        if (show.startsWith('registration')) {
            values.email_confirm = values.email_confirm || values.email;
            values.password_confirm = values.password_confirm || values.password;
        }

        const response = await request(show, values);
        if (!response) {
            return;
        }

        if (show === 'recoverySend') {
            setNotification(true);
            setRecoveryEmail(values.email);
            setShow('recoveryVerify');
        } else if (show === 'recoveryVerify') {
            setShow('recoverySuccess');
        }
    }

    async function request(currentScreen: AuthModalScreen, values: any) {
        if (loading) {
            return;
        }

        setErrors(null);
        setLoading(true);

        let response;
        if (currentScreen === 'login') {
            response = await api.login(values);
        } else if (currentScreen === 'recoverySend') {
            response = await api.post('auth/recovery-send', undefined, values);
        } else if (currentScreen === 'recoveryVerify') {
            response = await api.post('auth/recovery-verify', undefined, {
                ...values,
                email: recoveryEmail,
            });
        } else if (currentScreen.startsWith('registration')) {
            response = await api.registration(values);
        }

        setLoading(false);
        if (response?.errors) {
            setErrors(response.errors);
        }
        return !response || response.errors ? null : response;
    }

    function handleGoogleOAuth(source: 'login' | 'registration') {
        if (loading) {
            return;
        }

        setErrors(null);
        const authApiBaseUrl = resolveOAuthApiBaseUrl(process.env.NEXT_PUBLIC_AUTH_API_URL);
        if (!authApiBaseUrl) {
            setErrors({
                detail: 'OAuth is unavailable right now. Please continue with email/password.',
            });
            return;
        }

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${authApiBaseUrl}/auth/login/google`;
        form.style.display = 'none';

        const addField = (name: string, value: string) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value;
            form.appendChild(input);
        };

        const returnUrl = `${window.location.pathname}${window.location.search}` || '/';
        const subscribeToNews =
            source === 'registration'
                ? Boolean((document.getElementById('subscribe_to_news') as HTMLInputElement | null)?.checked)
                : false;

        addField('return_url', returnUrl.startsWith('/') ? returnUrl : '/');
        addField('subscribe_to_news', String(subscribeToNews));

        document.body.appendChild(form);
        form.submit();
    }

    return (
        <Container data-testid="auth-modal">
            <Content>
                <CloseButton aria-label="Close" disabled={loading} onClick={handleClose}>
                    <svg fill="currentColor" width="18" height="18" viewBox="0 0 24 24">
                        <path d="M18.3 5.71a.9959.9959 0 0 0-1.41 0L12 10.59 7.11 5.7a.9959.9959 0 0 0-1.41 0c-.39.39-.39 1.02 0 1.41L10.59 12 5.7 16.89c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L12 13.41l4.89 4.89c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z" />
                    </svg>
                </CloseButton>

                {loading && (
                    <Loader width="40" height="40" viewBox="22 22 44 44">
                        <LoaderCircle cx="44" cy="44" r="20" />
                    </Loader>
                )}

                <Form $loading={loading} onChange={() => setErrors(null)} onSubmit={handleSubmit}>
                    {show === 'login' && (
                        <>
                            <Title>Log in</Title>
                            <Row>
                                <OAuthButton
                                    data-testid="auth-modal-oauth-login"
                                    disabled={loading}
                                    type="button"
                                    onClick={() => handleGoogleOAuth('login')}
                                >
                                    <GoogleIcon />
                                    Log in with Google
                                </OAuthButton>
                            </Row>
                            <ModalDivider>or use your account</ModalDivider>
                            <TextField
                                disabled={loading}
                                name="email"
                                onChange={e => setLoginEmail(e.target.value)}
                                placeholder="Work email"
                                type="email"
                            />
                            {errors?.email && <Text $error>{errors.email}</Text>}
                            <TextField disabled={loading} name="password" placeholder="Password" type="password" />
                            {errors?.password && <Text $error>{errors.password}</Text>}
                            {errors?.detail && <Text $error>{errors.detail}</Text>}
                            <Row>
                                <CheckboxWrapper>
                                    <input disabled={loading} id="remember_me" name="remember_me" type="checkbox" />
                                    <label htmlFor="remember_me">Remember me</label>
                                </CheckboxWrapper>
                                <AuthSwitchButton
                                    as="a"
                                    aria-disabled={loading || undefined}
                                    data-testid="auth-modal-forgot-password"
                                    href={resolveForgotPasswordUrl(loginEmail)}
                                    tabIndex={loading ? -1 : undefined}
                                >
                                    Forgot your password?
                                </AuthSwitchButton>
                            </Row>
                            <Row>
                                <PrimaryButton $tone="dark" disabled={loading} type="submit">
                                    Log in
                                </PrimaryButton>
                            </Row>
                            <ModalDivider />
                            <Row>
                                <Text>Not a member yet?</Text>
                            </Row>
                            <Row>
                                <AuthModalButton
                                    data-testid="auth-modal-join-now"
                                    disabled={loading}
                                    screen={screen === 'registrationPremium' ? 'registrationPremium' : 'registration'}
                                    onClick={setShow}
                                >
                                    Join now
                                </AuthModalButton>
                            </Row>
                        </>
                    )}

                    {show === 'recoverySend' && (
                        <>
                            <Title>Recover your password</Title>
                            <Text $center $size={16}>
                                Enter your email to receive a verification code.
                            </Text>
                            <TextField disabled={loading} name="email" placeholder="Work email" type="email" />
                            {errors?.email && <Text $error>{errors.email}</Text>}
                            {errors?.detail && <Text $error>{errors.detail}</Text>}
                            <Row>
                                <PrimaryButton $tone="yellow" disabled={loading} type="submit">
                                    Send code
                                </PrimaryButton>
                            </Row>
                            <ModalDivider />
                            <Row>
                                <Text>Remembered your password?</Text>
                            </Row>
                            <Row>
                                <AuthModalButton disabled={loading} screen="login" onClick={setShow}>
                                    Log in
                                </AuthModalButton>
                            </Row>
                        </>
                    )}

                    {show === 'recoveryVerify' && (
                        <>
                            <Title>Verify code</Title>
                            <Text $center $size={16}>
                                Enter the code sent to {recoveryEmail}.
                            </Text>
                            <TextField disabled={loading} name="code" placeholder="Verification code" />
                            {errors?.code && <Text $error>{errors.code}</Text>}
                            <TextField
                                disabled={loading}
                                name="new_password"
                                placeholder="New password"
                                type="password"
                            />
                            {errors?.new_password && <Text $error>{errors.new_password}</Text>}
                            <TextField
                                disabled={loading}
                                name="new_password_confirm"
                                placeholder="Confirm new password"
                                type="password"
                            />
                            {errors?.new_password_confirm && <Text $error>{errors.new_password_confirm}</Text>}
                            {errors?.detail && <Text $error>{errors.detail}</Text>}
                            <Row>
                                <PrimaryButton $tone="yellow" disabled={loading} type="submit">
                                    Reset password
                                </PrimaryButton>
                            </Row>
                            <Row>
                                <AuthSwitchButton disabled={loading} type="button" onClick={handleRecoveryResend}>
                                    Resend code
                                </AuthSwitchButton>
                            </Row>
                        </>
                    )}

                    {show === 'recoverySuccess' && (
                        <>
                            <Title>Password updated</Title>
                            <Text $center $size={16}>
                                Your password has been reset successfully.
                            </Text>
                            <Row>
                                <AuthModalButton disabled={loading} screen="login" onClick={setShow}>
                                    Log in
                                </AuthModalButton>
                            </Row>
                        </>
                    )}

                    {(show === 'registration' || show === 'registrationPremium') && (
                        <>
                            <Title>{show === 'registrationPremium' ? 'Join now' : 'Join for free'}</Title>
                            <Text $center $size={16}>
                                {show === 'registrationPremium'
                                    ? 'Register a premium account'
                                    : 'Please create an account to access Kromatic'}
                            </Text>
                            <Row>
                                <Text $size={13}>Already have an account?</Text>
                                <AuthModalButton disabled={loading} screen="login" variant="text" onClick={setShow}>
                                    Log in
                                </AuthModalButton>
                            </Row>
                            <Row>
                                <OAuthButton
                                    data-testid="auth-modal-oauth-signup"
                                    disabled={loading}
                                    type="button"
                                    onClick={() => handleGoogleOAuth('registration')}
                                >
                                    <GoogleIcon />
                                    Sign up with Google
                                </OAuthButton>
                            </Row>
                            <ModalDivider>or use your account</ModalDivider>
                            <TextField disabled={loading} name="name" placeholder="Your name" />
                            {errors?.name && <Text $error>{errors.name}</Text>}
                            <TextField disabled={loading} name="email" placeholder="Work email" type="email" />
                            {errors?.email && <Text $error>{errors.email}</Text>}
                            <TextField disabled={loading} name="password" placeholder="Password" type="password" />
                            {errors?.password && <Text $error>{errors.password}</Text>}
                            {errors?.detail && <Text $error>{errors.detail}</Text>}
                            <Row>
                                <CheckboxWrapper>
                                    <input
                                        defaultChecked
                                        disabled={loading}
                                        id="subscribe_to_news"
                                        name="subscribe_to_news"
                                        type="checkbox"
                                    />
                                    <label htmlFor="subscribe_to_news">Subscribe to Kromatic Newsletters</label>
                                </CheckboxWrapper>
                            </Row>
                            <Row>
                                <PrimaryButton $tone="yellow" disabled={loading} type="submit">
                                    Join now
                                </PrimaryButton>
                            </Row>
                        </>
                    )}
                </Form>

                <Notification $show={notification}>Verification code sent successfully</Notification>
            </Content>
            <Backdrop onClick={handleClose} />
        </Container>
    );
}
